/**
 * The parties on one deal, and the CRM behind them
 * (`backend/app/services/deal_parties.py`).
 *
 * The Parties tab used to build its cards out of the loan row alone. That row
 * names a sponsor and a broker and has no field at all for their numbers,
 * sites or addresses — `sponsor_phone`, `sponsor_email` and `broker_email`
 * were read on every render and are written by nothing — so the tab printed
 * "Not on record" over details the CRM demonstrably held, on people the CRM
 * had already captured off this deal's own correspondence.
 *
 * This is the supply. Every party carries `contactId`: the CRM row behind it,
 * which is what makes an edit here the same write the CRM screen makes. There
 * is one directory, and both screens read and write it.
 */
import api from './api'

export interface DealPartyPhones {
  primary: string | null
  direct: string | null
  mobile: string | null
  office: string | null
}

/** Where a party's website came from. `email_domain` is derived from the
 *  addresses on record and is labelled as such on screen — a domain shown as
 *  though somebody entered a website is a claim nobody made. */
export type WebsiteBasis = 'recorded' | 'email_domain' | null

export interface DealPartyMessage {
  id: string
  type: string
  subject: string | null
  preview: string | null
  timestamp: string | null
  direction: string | null
  user: string | null
}

export interface DealParty {
  /** The slot this party fills — `sponsor`, `broker`, or a CRM contact id for
   *  somebody the loan row has no column for. A second party named in the
   *  same statement ("X LLC and Y Investments") is `sponsor:2`. */
  id: string
  kind: string
  name: string
  /** Every role this one entity holds on the deal. A firm that is the
   *  sponsor and the guarantor is one entry carrying both. */
  roles?: string[]
  /** The slot kinds folded into this entry, for the type filter. */
  kinds?: string[]
  /** Other deal slots the same entity fills; a rename or removal writes
   *  them too. */
  alsoSlots?: string[]
  /** The rule that typed this party as an organisation or a person. */
  entityBasis?: string
  /** What makes two statements one party (`org:…` / `person:…`). */
  partyKey?: string
  /** An organisation's legal name as it compares; null for a person. */
  entityKey?: string | null
  /** Where the organisation came from — the CRM record, the deal record,
   *  the deal's email, or a proposal a person has yet to confirm. */
  companyBasis?: string | null
  /** The organisation's party key, to link a person to its node. */
  companyKey?: string | null
  /** The CRM row behind this party, when one resolves. Null means there is
   *  nowhere to record contact details yet, and the screen says so rather than
   *  offering fields that would save nothing. */
  contactId: string | null
  contactSource: string | null
  /** The deal columns this party's NAME writes to. Empty for a party the loan
   *  row has no field for. */
  dealFields: string[]
  company: string | null
  title: string | null
  contact: string | null
  /** Optional recorded portrait; omitted by backends without photo support. */
  portraitUrl?: string | null
  email: string | null
  emails: string[]
  phone: string | null
  phones: DealPartyPhones
  address: string | null
  location: string | null
  website: string | null
  websiteBasis: WebsiteBasis
  linkedIn: string | null
  companyDomain: string | null
  isEntity: boolean
  isInternal: boolean
  score: number | null
  /** How the CRM saw them on THIS deal — sender, copied, named as the
   *  sponsor's contact. Empty where no CRM row backs the party. */
  capacities: string[]
  editedFields: string[]
  editedBy: string | null
  needsReview: boolean
  communications: DealPartyMessage[]
  notes: string | null
  facts: [string, string][]
}

export interface DealPartyRoster {
  loanId: string
  parties: DealParty[]
  /** Which stores the roster is drawn from, so a party a reader expects and
   *  cannot find explains itself rather than looking lost. */
  sourcedFrom: string[]
  crmContactCount: number
  /** Required parties the deal does not name — today only the Sponsor /
   *  Borrower. Said on screen as missing rather than left to vanish. */
  missingRequired?: { id: string; kind: string }[]
  /** Whether the lender's own staff can be recognised at all. Without email
   *  domains or people configured, desk staff read as outside parties here. */
  identitySuppression?: { emailDomains: number; people: number; staffConfigured: boolean }
}

/** A correction. Absent keys are left alone — a form that submitted every
 *  field would blank whatever it did not show. */
export interface DealPartyEdit {
  name?: string
  company?: string
  title?: string
  email?: string
  phone?: string
  directPhone?: string
  mobilePhone?: string
  officePhone?: string
  website?: string
  linkedIn?: string
  address?: string
  city?: string
  state?: string
  postalCode?: string
  notes?: string
  isEntity?: boolean
}

export interface DealPartyCreate {
  name: string
  slot?: string
  kind?: string
  company?: string
  title?: string
  email?: string
  phone?: string
  website?: string
  address?: string
  notes?: string
  isEntity?: boolean
  /** Replace the name an occupied Sponsor / Broker slot already holds. The
   *  server refuses an add into an occupied slot (409 `slot_occupied`) unless
   *  this says the replacement is intended. */
  replace?: boolean
}

/** One deal field an add would change, worked out before the write. */
export type SlotChange = { field: string; from: string | null; to: string | null; replaces: string | null }

/** The server's refusal to overwrite an occupied party slot silently. `current`
 *  is what the slot holds; `replaces` the part of a compound statement the add
 *  replaces; `changes` every field that would move (a borrower that only
 *  mirrors the sponsor moves with it); `message` says all of it in words. */
export type SlotOccupied = {
  code: 'slot_occupied'
  slot: string
  current: string
  message: string
  replaces?: string | null
  changes?: SlotChange[]
}

export function slotOccupiedOf(error: unknown): SlotOccupied | null {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response
  const detail = response?.data?.detail as Partial<SlotOccupied> | undefined
  if (response?.status === 409 && detail && detail.code === 'slot_occupied') return detail as SlotOccupied
  return null
}

/** Query parameters for a party write: the CRM row behind it, and the other
 *  slots the same entity fills (`also=guarantor&also=sponsor:2`). */
function partyParams(contactId: string | null | undefined, also: string[]) {
  const params: Record<string, string | string[]> = {}
  if (contactId) params.contact_id = contactId
  if (also.length) params.also = also
  return Object.keys(params).length ? params : undefined
}

export const dealPartyService = {
  list: (loanId: string): Promise<DealPartyRoster> =>
    api.get(`/loans/${loanId}/parties`).then((r) => r.data as DealPartyRoster),

  /** Add a party as a CRM contact linked to this deal.
   *
   *  A contact, never a master: adding somebody on this tab does not mint a
   *  broker or a sponsor master. (Promotion does create a sponsor master for a
   *  deal's admissible sponsor name; this path does not.) */
  add: (loanId: string, body: DealPartyCreate) =>
    api
      .post(`/loans/${loanId}/parties`, body)
      .then((r) => r.data as { contactId: string; dealFieldsWritten: string[] }),

  /** Correct a party. The name goes to the deal's own columns with lineage;
   *  everything else goes to the CRM contact through the CRM's own edit path,
   *  so the same correction shows on the directory screen. */
  edit: (
    loanId: string,
    slot: string,
    body: DealPartyEdit,
    contactId?: string | null,
    also: string[] = [],
  ) =>
    api
      .patch(`/loans/${loanId}/parties/${encodeURIComponent(slot)}`, body, {
        params: partyParams(contactId, also),
        paramsSerializer: { indexes: null },
      })
      .then((r) => r.data as { dealFieldsWritten: string[]; status: string }),

  /** Take a party off THIS deal. The contact stays in the directory: they were
   *  on other deals, and removing somebody from one transaction is not a
   *  statement about the person. */
  remove: (loanId: string, slot: string, contactId?: string | null, also: string[] = []) =>
    api
      .delete(`/loans/${loanId}/parties/${encodeURIComponent(slot)}`, {
        params: partyParams(contactId, also),
        paramsSerializer: { indexes: null },
      })
      .then((r) => r.data as { dealFieldsCleared: string[]; contactUnlinked: boolean }),
}

/** The roster as a CSV a desk can hand to someone. Every column the screen
 *  shows, so the file and the page agree. */
export function partiesToCsv(roster: DealPartyRoster): string {
  const headers = [
    'Role', 'Name', 'Company', 'Title', 'Primary contact', 'Email', 'Other emails',
    'Phone', 'Direct', 'Mobile', 'Office', 'Address', 'Location', 'Website',
    'Website basis', 'LinkedIn', 'Capacity on this deal', 'CRM contact id',
  ]
  const cell = (value: unknown): string => {
    const text = value === null || value === undefined ? '' : String(value)
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  const rows = roster.parties.map((party) => [
    party.kind, party.name, party.company, party.title, party.contact,
    party.email, party.emails.slice(1).join('; '),
    party.phones.primary, party.phones.direct, party.phones.mobile, party.phones.office,
    party.address, party.location, party.website, party.websiteBasis, party.linkedIn,
    party.capacities.join('; '), party.contactId,
  ])
  return [headers, ...rows].map((row) => row.map(cell).join(',')).join('\n')
}
